import type { ReactNode } from 'react';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { Input } from '@/components/ui/input';

import {
  BULK_DELETE_CONFIRM_PHRASE,
  type BulkDeleteState,
  formatBulkCount,
} from './use-bulk-delete';

/**
 * The confirmation for a bulk delete, on the shared `ConfirmDeleteDialog`.
 *
 * Above `BULK_DELETE_TYPED_CONFIRM_THRESHOLD` records it also asks the operator
 * to type DELETE — at that size a misclick is expensive and the count in the
 * title is easy to skim past.
 */
export function BulkDeleteDialog({
  bulk,
  description,
}: {
  bulk: BulkDeleteState;
  /**
   * What actually happens to these records. Defaults to "permanently delete …
   * cannot be undone" — pass the entity's real consequence when it differs
   * (deactivated instead of deleted, bookings kept, other branches affected).
   */
  description?: ReactNode;
}) {
  const { count, noun, needsTypedConfirmation } = bulk;

  const defaultDescription = needsTypedConfirmation ? (
    <>
      You are about to permanently delete{' '}
      <strong>{formatBulkCount(count, noun)}</strong>. This action cannot be
      undone.
    </>
  ) : (
    <>
      This will permanently delete{' '}
      {count === 1
        ? `the selected ${noun.singular}`
        : `${count} selected ${noun.plural}`}
      . This action cannot be undone.
    </>
  );

  return (
    <ConfirmDeleteDialog
      confirmDisabled={!bulk.canConfirm}
      confirmLabel="Delete"
      description={
        <>
          {description ?? defaultDescription}
          {needsTypedConfirmation && (
            <>
              {' '}
              Type <strong>{BULK_DELETE_CONFIRM_PHRASE}</strong> below to
              confirm.
            </>
          )}
        </>
      }
      isPending={bulk.isDeleting}
      onConfirm={() => void bulk.confirm()}
      onOpenChange={bulk.onDialogOpenChange}
      open={bulk.dialogOpen}
      title={`Delete ${formatBulkCount(count, noun)}?`}
    >
      {needsTypedConfirmation && (
        <Input
          aria-label={`Type ${BULK_DELETE_CONFIRM_PHRASE} to confirm`}
          autoFocus
          className="mt-2"
          onChange={(e) => bulk.setConfirmText(e.target.value)}
          placeholder={`Type ${BULK_DELETE_CONFIRM_PHRASE} to confirm`}
          value={bulk.confirmText}
        />
      )}
    </ConfirmDeleteDialog>
  );
}
